
import moment from 'moment'
import { mapGetters } from 'vuex'
import promoData from '@/data/promo.json'
import { getABBucket } from "@/utils/abTest";

// Only show the countdown once the deadline is this close
const SHOW_WITHIN_HOURS = 48

export default {
  data: () => ({
    time: {Days:"0",Hours:"0",Minutes:"0",Seconds:"0"},
    zeroTime: {Days:"00",Hours:"00",Minutes:"00",Seconds:"00"},
    i: 0,
    ended: false,
    hideForAbTest: false,
    showCountdown: false
  }),

  computed: {
    ...mapGetters({
      detectedTimezone: 'geolocation/detectedTimezone',
      detectedRegion: 'geolocation/region',
      userRegion: 'user/region',
    }),
    region(){
        return this.userRegion === 'default' ? this.detectedRegion : this.userRegion
    },
    promoRegion() {
      // Find the region in promo.json that matches the user's region, fallback to 'uk'
      return promoData.regions.find(r => r.code === this.region) ||
             promoData.regions.find(r => r.code === 'en');
    },
    promoBanner() {
      // Use specialOffer if enabled, otherwise standardOffer
      if (this.promoRegion && this.promoRegion.specialOffer && this.promoRegion.specialOffer.enabled) {
        return this.promoRegion.specialOffer;
      }
      return this.promoRegion ? this.promoRegion.standardOffer : {};
    },
    saleDays() {
      // Use saleDays from the current promoBanner, fallback to [1,4] if not set or empty
      if (this.promoBanner && Array.isArray(this.promoBanner.saleDays) && this.promoBanner.saleDays.length) {
        return this.promoBanner.saleDays;
      }
      return [1, 4];
    },
    bgColour() {
      // You can set a default or use a color from promoBanner if available
      return '#2EA18B';
    }
  },

  mounted() {
    // A/B gate — dormant while the edge function is off (cookie won't be set, so this stays false)
    if (getABBucket('tefl_countdown_bucket') === 'hide') {
      this.hideForAbTest = true
      this.$emit('visibility', false)
      return
    }

    const tz = this.detectedTimezone || 'Europe/London'
    const now = moment().tz(tz)
    let deadline = null

    // During an active campaign, only count down if this region is participating
    if (this.$campaign && this.$campaign.isActive && this.$campaign.endDate) {
      const offer = this.$campaign.offerForRegion(this.region)

      if (offer && offer.offerText) {
        const campaignEnd = moment.tz(this.$campaign.endDate, tz).endOf('day')

        if (now.isAfter(campaignEnd)) {
          // Campaign over — stay hidden entirely
          this.ended = true
          this.$emit('visibility', false)
          return
        }

        // The 48h window is measured against the campaign end date
        deadline = campaignEnd

        if (Array.isArray(offer.saleDays) && offer.saleDays.length) {
          // Campaign has its own reset days — resets on those weekdays, but never counts past the campaign end
          const nextReset = this.getNextSaleDate(offer.saleDays)
          this.countdown = nextReset.isAfter(campaignEnd) ? campaignEnd : nextReset
        } else {
          // No reset days set — one countdown running straight to the campaign end date
          this.countdown = campaignEnd
        }
      }
    }

    // Regular sale day countdown (campaign inactive OR region not participating)
    if (!deadline) {
      const nextSaleDate = this.getNextSaleDate()
      if (now.isAfter(nextSaleDate)) {
        // Deadline passed — stay hidden entirely
        this.ended = true
        this.$emit('visibility', false)
        return
      }
      deadline = nextSaleDate
      this.setCountdown()
    }

    // Show only in the final 48 hours before the deadline; stay hidden before that
    if (deadline.diff(now, 'hours', true) <= SHOW_WITHIN_HOURS) {
      this.showCountdown = true
      this.update()
    }

    // Let parents collapse any surrounding chrome (labels, promo bars, spacers)
    this.$emit('visibility', this.showCountdown)
  },

  methods: {
    getNextSaleDate(days) {
      // Accepts an optional days array so the campaign branch can reuse this with its own reset days
      const saleDays = days || this.saleDays;
      const tz = this.detectedTimezone || 'Europe/London';
      const now = moment().tz(tz);
      let soonest = null;

      saleDays.forEach(dayNum => {
        const sale = moment().tz(tz).day(dayNum).hour(23).minute(59).second(59).millisecond(0);
        // If the sale day has already passed this week, move to next week
        if (sale.isBefore(now)) {
          sale.add(7, 'days');
        }
        if (!soonest || sale.isBefore(soonest)) {
          soonest = sale;
        }
      });

      return soonest;
    },

    setCountdown() {
      this.countdown = this.getNextSaleDate();
    },

    update() { 
      const time = {Days:"00",Hours:"00",Minutes:"00",Seconds:"00"}
      this.frame = window.requestAnimationFrame(this.update.bind(this));
      if ( this.i++ % 10 ) { return; }
      const t = moment().tz(this.detectedTimezone || 'Europe/London');
      if ( this.countdown ) { 
        const diff = this.countdown.diff(t);
        time.Days = Math.floor(diff / (1000 * 60 * 60 * 24));
        time.Hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
        time.Minutes = Math.floor((diff / 1000 / 60) % 60);
        time.Seconds = Math.floor((diff / 1000) % 60);
      } else {
        time.Days = undefined;
        time.Hours = t.hours() % 13;
        time.Minutes = t.minutes();
        time.Seconds = t.seconds();
      }
      this.time = time
      return this.time;
    }
  }
}
